import Bugsnag from '@bugsnag/js'
import React from 'react'
import { useDispatch } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { setStep } from './state'

import {
  Text,
  Main,
  Button,
  HorizontalRule,
  ButtonHTMLLink,
} from '../../../components'
import { urls } from '../urls'
import {
  useShowJoinableOrganizationsQuery,
  useShowJoinedOrganizationsQuery,
} from '../../../remote/graphql'
import { CreateSteps } from './CreateSteps'
import { Avatar } from '../../../components/Avatar'
import { appProtocol, appDomain, appPath } from '../../../config'
import { getStructureCacheKeyFromUrl } from 'utils'
import { generatePath } from 'react-router'

export function JoinableTeams({
  onCreateAnyway,
}: {
  onCreateAnyway: () => void
}) {
  const {
    data: joinableOrganizationData,
    loading: joinableOrganizationLoading,
  } = useShowJoinableOrganizationsQuery({
    onError(error) {
      // just notify and skip this step
      Bugsnag.notify('Show Joinable Orgs query error', (event) =>
        event.addMetadata('GraphQL error', error)
      )
      onCreateAnyway()
    },
  })

  const { data: joinedOrganizationData, loading: joinedOrganizationLoading } =
    useShowJoinedOrganizationsQuery({
      onError(error) {
        // just notify and skip this step
        Bugsnag.notify('Show Joined Orgs query error', (event) =>
          event.addMetadata('GraphQL error', error)
        )
        onCreateAnyway()
      },
    })

  const joinedOrganizations = React.useMemo(() => {
    return joinedOrganizationData?.showJoinedOrganizations ?? []
  }, [joinedOrganizationData])

  const joinableOrganizations = React.useMemo(() => {
    return joinableOrganizationData?.showJoinableOrganizations ?? []
  }, [joinableOrganizationData])

  const loading = joinableOrganizationLoading || joinedOrganizationLoading

  const structureCacheKey = getStructureCacheKeyFromUrl()

  React.useEffect(() => {
    if (loading) {
      return
    }

    if (
      joinedOrganizations.length === 0 &&
      joinableOrganizations.length === 0
    ) {
      onCreateAnyway()
    }
  }, [loading, joinedOrganizations, joinableOrganizations, onCreateAnyway])

  if (loading) {
    return (
      <Main.Container style={{ textAlign: 'center' }}>
        <Main.Title>
          <FormattedMessage
            id="boarding.create.joinableTeams.loading.title"
            defaultMessage="Checking for teams you can join..."
          />
        </Main.Title>
      </Main.Container>
    )
  }

  return (
    <Main.Container style={{ textAlign: 'center' }}>
      <Main.Title>
        <FormattedMessage
          id="boarding.create.joinableTeams.title"
          defaultMessage="Joinable teams"
        />
      </Main.Title>
      <Text.Corpus style={{ lineHeight: '1.8em' }}>
        {joinedOrganizations.length > 0 && (
          <>
            <Text.Section className="shade-05">
              <FormattedMessage
                id="boarding.create.joinedTeams.description"
                defaultMessage="You've already joined teams listed below:"
              />
            </Text.Section>
            {joinedOrganizations.map((organization) => {
              const url = `${urls.getOrganizationAppPath(
                {
                  appDomain,
                  appProtocol,
                  appPath: structureCacheKey
                    ? generatePath(
                        '/app/wikiGeneratorScaffoldStructure/:structureCacheKey',
                        { structureCacheKey }
                      )
                    : appPath,
                },
                organization.domain
              )}`
              return (
                <div
                  key={organization.id}
                  className="flexbox flexbox-align-center margin-vertical-12 flexbox-space-between"
                >
                  <div className="flexbox flexbox-align-center flexbox-grow flexbox-column-gap-12">
                    {organization.photoURL && (
                      <Avatar
                        className="margin-right-12"
                        organizationName={organization.name}
                        organizationPhotoUrl={organization.photoURL}
                      />
                    )}
                    <div className="flexbox flexbox-column flexbox-grow text-align-left flexbox-row-gap-2">
                      <div className="font-weight-bold shade-05 font-size-normal line-height-normal">
                        {organization.name}
                      </div>
                      {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                      <div className="shade-20 font-size-normal line-height-normal">{`${organization.domain}.${appDomain}`}</div>
                    </div>
                  </div>
                  <ButtonHTMLLink
                    variant="primary"
                    href={url}
                    className="margin-left-12 line-height-normal radius-large"
                  >
                    <FormattedMessage
                      id="boarding.create.joinedTeams.open"
                      defaultMessage="Open"
                    />
                  </ButtonHTMLLink>
                </div>
              )
            })}
            <HorizontalRule />
          </>
        )}
        {joinableOrganizations.length > 0 && (
          <>
            <Text.Section className="shade-05">
              {joinedOrganizations.length > 0 ? (
                <FormattedMessage
                  id="boarding.create.joinableTeams.descriptionType1"
                  defaultMessage="Some teams are also available for you to join:"
                />
              ) : (
                <FormattedMessage
                  id="boarding.create.joinableTeams.descriptionType2"
                  defaultMessage="Some teams are already available for you to join:"
                />
              )}
            </Text.Section>
            {joinableOrganizations.map(({ organization, url }) => {
              return (
                <div
                  key={organization.id}
                  className="flexbox flexbox-align-center margin-vertical-12 flexbox-space-between"
                >
                  <div className="flexbox flexbox-align-center flexbox-grow flexbox-column-gap-12">
                    {organization.photoURL && (
                      <Avatar
                        className="margin-right-12"
                        organizationName={organization.name}
                        organizationPhotoUrl={organization.photoURL}
                      />
                    )}
                    <div className="flexbox flexbox-column flexbox-grow text-align-left flexbox-row-gap-2">
                      <div className="font-weight-bold shade-05 font-size-normal line-height-normal">
                        {organization.name}
                      </div>
                      {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                      <div className="shade-20 font-size-normal line-height-normal">{`${organization.domain}.${appDomain}`}</div>
                    </div>
                  </div>
                  <ButtonHTMLLink
                    variant="primary"
                    href={url}
                    className="margin-left-12 line-height-normal radius-large"
                  >
                    <FormattedMessage
                      id="boarding.create.joinableTeams.join"
                      defaultMessage="Join"
                    />
                  </ButtonHTMLLink>
                </div>
              )
            })}
            <HorizontalRule />
          </>
        )}
        <Button className="width-full" onClick={onCreateAnyway}>
          <FormattedMessage
            id="boarding.create.joinableTeams.createAnyway"
            defaultMessage="Create workspace"
          />
        </Button>
      </Text.Corpus>
    </Main.Container>
  )
}

export function HookedJoinableTeams() {
  const dispatch = useDispatch()
  return (
    <JoinableTeams
      onCreateAnyway={() => dispatch(setStep(CreateSteps.Password))}
    />
  )
}
